import { Component, Fragment } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    RecentChatRoster,
    RecentChatUpdateAction,
    RecentChatUpdateNotification,
    RecentChatAction,
} from '../../Actions/RecentChatActions';
import {
    loaderSVG,
    Nochat,
    EmptySearch,
    IconEmptyArchive,
    StartVector,
    IconNoInternet,
    Search,
    Close,
} from '../../assets/images';
import { isRenderMessageType, sortBydate } from '../../Helpers/Chat/RecentChat';
import {
    NO_RECENT_CHAT_INFO,
    NO_RECENT_CLICK_ON_INFO,
    NO_RECENT_SEARCH_CONTACTS_INFO,
    NO_RESULTS_FOUND,
} from '../../Components/processENV';
import SDK from '../../Components/SDK';
import Title from '../../Components/WebChat/Common/BrowserTitle';
import { Store } from '../../store';
import { UpdateArchiveChatDataAction } from '../../Actions/Common';
import { getValidSearchVal, isConnStateEqualTo } from '../../Helpers/Utility';
import { getUserInfoForSearch, getDataFromRoster } from '../../Helpers/Chat/User';
import { isSingleChat } from '../../Helpers/Chat/ChatHelper';
import {
    CHAT_FILTER,
    CONNECTION_STATE_CONNECTING,
    CONNECTION_STATE_CONN_FAILED,
    CONNECTION_STATE_ERROR_OCCURED,
} from '../../Helpers/Chat/Constant';
import { clearChatSearch, setChatSearch, setChatSearchFiltered } from '../../slice/chatSearchSlice';
import { setChatFilter } from '../../slice/chatFilterSlice';
import { useLanguage } from '../../localization/useLanguage';
import { i18n } from '../../localization/i18n';
import { countByChatFilter, matchesChatFilter } from '../../Helpers/Chat/ChatFilter';
import logger from '../../Helpers/logger';
import ChipsRow from './ChipsRow';
import ArchivedRow from './ArchivedRow';
import RosterContacts from './RosterContacts';
import RecentChat from './RecentChat';
import useMuteExpiry from './useMuteExpiry';

// FR59: fetch the next page once 70% of the collected records have been scrolled through.
const RECENT_CHAT_FETCH_THRESHOLD = 0.7;

// What the backend returns per call; a shorter page means there is nothing left to fetch.
const RECENT_CHAT_PAGE_SIZE = 10;

const LOADER_STYLE = {
    width: 80,
    height: 80,
};

class RecentChatSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loader: true,
            searchEnable: false,
            searchValue: '',
            recentChatNames: [],
            recentChatItems: [],
            filteredContacts: [],
            prevMessageTime: '',
            archiveOpen: false,
            isPaginating: false,
            hasMore: true,
        };
        this.timer = 0;
        this.chatExist = false;
    }

    filterUserNameFromRecent = recentChatArray => recentChatArray.map(chat => chat.fromUserId);

    filterRosterByUserNames = (recentChatArray, rosterDataArray) => {
        const recentChatNames = this.filterUserNameFromRecent(recentChatArray);
        return rosterDataArray.filter(profile => {
            const rosterJid = profile.username ? profile.username : profile.userId;
            return recentChatNames.indexOf(rosterJid) > -1;
        });
    };

    filterProfileFromRoster = (filteredRoaster, messageFrom) => {
        return getDataFromRoster(messageFrom);
    };

    filterProfileFromGroup = messageFrom => {
        const { groupsData: { data: groupsDataArray = [] } = {} } = this.props;
        return groupsDataArray?.find(groups => messageFrom === groups.groupId);
    };

    filterProfileFromBroadcast = messageFrom => {
        const { broadCastData: { data: { broadcastList: broadcastListArray = [] } = {} } = {} } = this.props;
        return broadcastListArray.find(broadcast => messageFrom === broadcast.jid);
    };

    getRoaster = (chatType = 'groupchat', messageFrom = '') => {
        const messageType = {
            groupchat: this.filterProfileFromGroup,
            broadcast: this.filterProfileFromBroadcast,
        };
        return messageType[chatType]?.(messageFrom);
    };

    constructRecentChatItems = (recentChatArray, filteredRoaster) => {
        let recent = [];
        sortBydate([...recentChatArray]).map(async chat => {
            let data = {};
            if (isSingleChat(chat.chatType)) {
                data = this.filterProfileFromRoster(filteredRoaster, chat.fromUserId);
            } else {
                data = this.getRoaster(chat.chatType, chat.fromUserId);
            }
            recent.push({
                recent: chat,
                roster: data || {},
            });
        });
        return recent.filter(eachmessage => eachmessage);
    };

    componentWillUnmount() {
        clearTimeout(this.timer);
        // The keyword does not survive leaving the list, unlike the selected tag, which
        // the user finds still applied on their return.
        this.props.clearChatSearch();
        this.props.setChatSearchFiltered(false);
    }

    getRecentChatsItems() {
        const {
            rosterData: { id: rosterId, data: rosterDataArray = [] } = {},
            recentChatData: { id: recentchatId, data: recentChatArray = [] } = {},
        } = this.props;
        const filteredRoaster = this.filterRosterByUserNames(recentChatArray, rosterDataArray);
        let recentChatItems = this.constructRecentChatItems(recentChatArray, filteredRoaster);
        let recentChatNames = this.filterUserNameFromRecent(recentChatArray);
        let unArchiveChats = recentChatArray.filter(obj => obj.archiveStatus === 0).length;
        let ArchiveChats = recentChatArray.filter(obj => obj.archiveStatus === 1).length;
        // A list that is mostly archived leaves too few visible rows to scroll, so the next
        // page is pulled straight away rather than waiting for a scroll that cannot happen.
        if (recentChatArray.length > 0 && ArchiveChats > 10 && unArchiveChats < 15) {
            this.fetchNextRecentChatsPage();
        }
        return { recentChatItems, recentChatNames, rosterId, recentchatId };
    }

    componentDidMount() {
        const { isArchived = false } = this.props.commonData?.data || {};
        if (isArchived) {
            this.setState({ archiveOpen: true });
        }

        let { recentChatItems, recentChatNames, recentchatId } = this.getRecentChatsItems();
        if (recentchatId) {
            if (recentChatItems.length > 0) {
                this.setState({
                    recentChatItems: recentChatItems,
                    recentChatNames: recentChatNames,
                    loader: false,
                });
            } else {
                this.setState({ loader: false });
            }
        }
    }

    componentDidUpdate(prevProps) {
        const {
            rosterData: { id: rosterId } = {},
            recentChatData: { id: recentchatId, refreshUnreadCount } = {},
            commonData: {
                data: { isArchived = false },
            },
            searchValue,
            searchFiltered,
        } = this.props;

        const isRefreshed = isArchived !== prevProps?.commonData?.data?.isArchived;

        // Not gated on isRefreshed: this pane unmounts when another rail tab is open, so
        // it can mount with the flag already set and would otherwise never catch up.
        if (isArchived !== this.state.archiveOpen) {
            this.setState({ archiveOpen: isArchived });
        }

        if (
            prevProps.rosterData.id !== rosterId ||
            (!prevProps.recentChatData.id && recentchatId) ||
            prevProps.recentChatData.id !== recentchatId ||
            isRefreshed
        ) {
            if (!recentchatId) return;
            let { recentChatItems, recentChatNames } = this.getRecentChatsItems();

            this.setState({
                recentChatItems: recentChatItems,
                recentChatNames: recentChatNames,
                loader: false,
                refreshUnreadCount: searchValue ? false : refreshUnreadCount,
            });
            this.props.updateRoster({ recentChatNames, recentChatItems });
            return;
        }
        if (!searchFiltered) {
            this.searchFilterList(searchValue, refreshUnreadCount);
        }

        if (
            prevProps?.broadCastData?.id &&
            this.props.broadCastData &&
            prevProps.broadCastData.id !== this.props.broadCastData.id &&
            this.props.broadCastData?.data?.broadcastList?.length !==
                prevProps.broadCastData?.data?.broadcastList?.length
        ) {
            const recentChatsRes = SDK.getRecentChatsByPagination();
            if (recentChatsRes && recentChatsRes.statusCode === 200) {
                Store.dispatch(RecentChatAction(recentChatsRes.data));
            }
            return;
        }

        if (prevProps.groupsData && this.props.groupsData && prevProps.groupsData.id !== this.props.groupsData.id) {
            this.updateGroupInfo();
        }
    }

    /**
     * handleGroupData() method to handle name and image of groups in realtime
     */
    updateGroupInfo = () => {
        let { recentChatItems } = this.state;

        if (recentChatItems.length === 0) return;
        const updateRecentChat = recentChatItems.map(chat => {
            const { recent, roster } = chat;
            if (recent.chatType !== 'groupchat') {
                return chat;
            }
            const groupId = roster.groupId || recent.fromUserId;
            const data = this.filterProfileFromGroup(groupId);
            return {
                recent: recent,
                roster: data || {},
            };
        });
        this.setState(
            {
                recentChatItems: updateRecentChat,
            },
            () => {
                let recentChatNames = this.state.recentChatNames;
                recentChatItems = this.state.recentChatItems;
                this.props.updateRoster({ recentChatNames, recentChatItems });
            },
        );
    };

    recentChatSearchFilter = searchWith => {
        const { recentChatItems } = this.state;
        this.chatExist = false;
        return recentChatItems.map(item => {
            const { roster } = item;
            const regexList = getUserInfoForSearch(roster);
            const updateSearch = regexList.find(str => {
                if (!str) return false;
                return str.search(new RegExp(searchWith, 'i')) !== -1;
            });
            if (updateSearch) {
                item.hidden = false;
                this.chatExist = true;
                return item;
            }
            item.hidden = true;
            return item;
        });
    };

    contactsSearch = searchTerm => {
        const { recentChatNames } = this.state;
        const {
            rosterData: { data },
        } = this.props;
        if (!data) return [];
        return data.filter(item => {
            const rosterJid = item.username ? item.username : item.userId;
            if (recentChatNames.indexOf(rosterJid) > -1) {
                return false;
            }

            const regexList = getUserInfoForSearch(item);
            return regexList.find(str => {
                if (!item.isFriend || !str) return false;
                return str.search(new RegExp(searchTerm, 'i')) !== -1;
            });
        });
    };

    searchFilterList = (searchValue, refreshUnreadCount) => {
        let searchWith = getValidSearchVal(searchValue);
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
            const updatedList = this.recentChatSearchFilter(searchWith);
            const filteredContacts = this.contactsSearch(searchWith);
            this.props.setChatSearch(searchValue);
            this.props.setChatSearchFiltered(true);
            this.setState({
                searchEnable: !!searchValue,
                filteredRecentChat: updatedList,
                filteredContacts: filteredContacts,
                refreshUnreadCount,
            });
        }, 0);
    };

    shouldComponentUpdate(nextProps) {
        const { callUserJID, messageData: { id, data: { messageType } = {} } = {} } = this.props;
        if (callUserJID !== nextProps.callUserJID) {
            return true;
        }
        const nextPropsMsgId = nextProps?.messageData?.id;
        const nextPropsMsgType = nextProps?.messageData?.data?.messageType;
        const checkExist = isRenderMessageType(nextPropsMsgType);
        return !(messageType && nextPropsMsgId !== id && checkExist);
    }

    handleRecentChatsScroll = event => {
        const { scrollTop, clientHeight, scrollHeight } = event.target;
        const scrolled = scrollTop + clientHeight;

        // FR59 fetches once 70% of the collected records have been scrolled through, so the
        // next page is on its way before the user reaches the end of the current one.
        if (scrolled < scrollHeight * RECENT_CHAT_FETCH_THRESHOLD) return;

        this.fetchNextRecentChatsPage();
    };

    /**
     * Loads the page of conversations after the oldest one on screen.
     *
     * Guarded against the repeat calls a scroll event fires while a request is still open,
     * and against paging past the end: a page shorter than the requested size means the
     * server has nothing left, so `hasMore` closes the door instead of every later scroll
     * asking again for the same records.
     */
    fetchNextRecentChatsPage = async () => {
        const { recentChatData: { data: recentChatArray = [] } = {} } = this.props;
        const { isPaginating, hasMore } = this.state;

        if (isPaginating || !hasMore || !Array.isArray(recentChatArray) || recentChatArray.length === 0) return;

        const messageTime = Math.min(...recentChatArray.map(obj => obj.messageTime));
        if (Number(this.state.prevMessageTime) === Number(messageTime)) return;

        this.setState({ isPaginating: true });
        try {
            const recentChats = await SDK.getRecentChatsByPagination({ messageTime });
            const page = recentChats?.data || [];
            Store.dispatch(RecentChatAction(page));
            this.setState({
                prevMessageTime: messageTime,
                hasMore: page.length >= RECENT_CHAT_PAGE_SIZE,
            });
        } catch (error) {
            // Leave hasMore alone so a dropped request can be retried by scrolling again.
            logger.warn('[RecentChat] pagination failed', { messageTime, error });
        } finally {
            this.setState({ isPaginating: false });
        }
    };

    // The overflow menu owns the flag that opens this list, so closing clears that flag
    // rather than toggling local state the menu would not see.
    handleCloseArchive = () => {
        Store.dispatch(UpdateArchiveChatDataAction({ isArchived: false }));
    };

    // TODO: open the Chat Tag screen once FR188 exists. Kept inert so the icon FR59
    // requires is present without pretending to navigate anywhere.
    handleManageTags = () => {};

    render() {
        const { recentChatItems, filteredRecentChat, searchEnable, filteredContacts, loader, archiveOpen } = this.state;
        const {
            handleConversationSectionDisplay,
            callUserJID,
            handleShowCallScreen,
            callType,
            messageData,
            callMode,
            searchValue,
            pageType,
            unreadCountData: { unreadDataObj = {} } = {},
            featureStateData,
            chatFilter = CHAT_FILTER.ALL,
        } = this.props;
        const { isRecentChatSearchEnabled = false } = featureStateData;
        const updatedRecentChat = searchEnable ? filteredRecentChat : recentChatItems;
        // Swaps only the list; the Chats header, search and new-chat button stay in place.
        const listPageType = pageType === 'recent' && archiveOpen ? 'archive' : pageType;
        const archivedChats = updatedRecentChat
            .filter(el => el?.recent?.archiveStatus === 1)
            .map(el => el?.recent?.fromUserId || '');

        // Chips live on the Chats pane only, and step aside while a search is running.
        const showChips = pageType === 'recent' && !archiveOpen && !searchValue;
        // Counted over the same set the chips filter, or the number would not match the rows.
        const chipScope = showChips ? updatedRecentChat.filter(el => el?.recent?.archiveStatus !== 1) : [];
        const chipCounts = showChips ? countByChatFilter(chipScope, unreadDataObj) : {};
        const isFiltering = showChips && chatFilter !== CHAT_FILTER.ALL;
        const filterMatchCount = isFiltering
            ? chipScope.filter(el => matchesChatFilter(el, chatFilter, unreadDataObj)).length
            : 0;
        // The filter emptied a list that does have chats — distinct from having none.
        const isFilterEmpty = isFiltering && chipScope.length > 0 && filterMatchCount === 0;

        if (loader) {
            return (
                <div className="loader-container">
                    <img src={loaderSVG} alt="loader" style={LOADER_STYLE} />
                </div>
            );
        }

        return (
            <div className="chat-list-container">
                {pageType === 'recent' && !archiveOpen && isRecentChatSearchEnabled && (
                    <ChatListSearch placeHolder={i18n.t('chat.search.PLACEHOLDER')} search={this.searchFilterList} />
                )}
                {showChips && (
                    <ChipsRow
                        active={chatFilter}
                        counts={chipCounts}
                        onSelect={this.props.setChatFilter}
                        onManageTags={this.handleManageTags}
                    />
                )}
                {(!this.props.isAppOnline ||
                    isConnStateEqualTo([
                        CONNECTION_STATE_CONNECTING,
                        CONNECTION_STATE_CONN_FAILED,
                        CONNECTION_STATE_ERROR_OCCURED,
                    ])) && (
                    <div className="networkoffline">
                        <i className="icon">
                            <IconNoInternet />
                        </i>
                        <div>
                            <h4>Computer not connected</h4>
                            <p>Make sure your computer has an active internet connection.....</p>
                        </div>
                    </div>
                )}
                {
                    <div
                        className={`chat-list ${
                            !this.props.isAppOnline ||
                            isConnStateEqualTo([
                                CONNECTION_STATE_CONNECTING,
                                CONNECTION_STATE_CONN_FAILED,
                                CONNECTION_STATE_ERROR_OCCURED,
                            ])
                                ? 'offline'
                                : ''
                        } ${pageType === 'archive' ? 'archive' : ''} ${
                            pageType === 'archive' && updatedRecentChat.length === 0 && !searchValue
                                ? ' no-chat-archive '
                                : ''
                        } ${pageType !== 'archive' && updatedRecentChat.length === 0 && !searchValue ? ' no-chat ' : ''} ${
                            showChips ? 'has-filter-chips' : ''
                        }`}
                        onScroll={e => this.handleRecentChatsScroll(e)}
                    >
                        {pageType === 'recent' && archiveOpen && (
                            <ArchivedRow count={archivedChats.length} onBack={this.handleCloseArchive} />
                        )}
                        <RecentChat
                            pageType={listPageType}
                            searchTerm={searchValue}
                            handleConversationSectionDisplay={handleConversationSectionDisplay}
                            recentChatItems={updatedRecentChat}
                            callUserJID={callUserJID}
                            callStatus={this.props.callStatus}
                            handleShowCallScreen={handleShowCallScreen}
                            messageData={messageData}
                            callType={callType}
                            callMode={callMode}
                            recentChatExist={this.chatExist}
                            refreshUnreadCount={this.state.refreshUnreadCount}
                            handlePopupState={this.props.handlePopupState}
                            archiveLength={archivedChats.length}
                            vCardData={this.props.vCardData}
                            chatFilter={chatFilter}
                            unreadDataObj={unreadDataObj}
                        />

                        {this.state.isPaginating && (
                            <div className="recent-chat-loading-more" role="status" aria-live="polite">
                                <img src={loaderSVG} alt="" />
                            </div>
                        )}

                        {!archiveOpen && (
                            <RosterContacts searchTerm={searchValue} filteredContacts={filteredContacts} />
                        )}

                        {!this.chatExist && filteredContacts.length === 0 && searchValue && (
                            <div
                                className={`no-search-record-found ${
                                    pageType === 'recent' && archivedChats.length ? 'has-archive' : ''
                                }`}
                            >
                                <div className={`norecent-chat`}>
                                    <i className="norecent-chat-img">
                                        <EmptySearch />
                                    </i>
                                    <h4>{NO_RESULTS_FOUND}</h4>
                                </div>
                            </div>
                        )}
                        {isFilterEmpty && <FilterEmptyState />}
                        {!isFilterEmpty &&
                            ((archivedChats.length === 0 && listPageType === 'archive') ||
                                (listPageType === 'recent' && updatedRecentChat.length - archivedChats.length === 0)) &&
                            !searchValue && (
                                <div
                                    className={`norecent-chat ${listPageType === 'recent' && archivedChats.length ? 'has-archive' : ''}`}
                                >
                                    {listPageType === 'archive' ? (
                                        <Fragment>
                                            <i className="norecent-chat-img">
                                                <IconEmptyArchive />
                                            </i>
                                            <h4>{i18n.t('chat.EMPTY_NO_DATA')}</h4>
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <i className="norecent-chat-img">
                                                {' '}
                                                <StartVector />{' '}
                                            </i>
                                            <h4>{NO_RECENT_CHAT_INFO}</h4>
                                            <h4>
                                                {NO_RECENT_CLICK_ON_INFO}
                                                <i>
                                                    <Nochat />
                                                </i>{' '}
                                                {NO_RECENT_SEARCH_CONTACTS_INFO}
                                            </h4>
                                        </Fragment>
                                    )}
                                </div>
                            )}
                    </div>
                }
                <Title />
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        featureStateData: state.featureStateData,
        rosterData: state.rosterData,
        recentChatData: state.recentChatData,
        singleChatMsgHistoryData: state.singleChatMsgHistoryData,
        groupsData: state.groupsData,
        messageData: state.messageData,
        broadCastData: state.broadCastData,
        isAppOnline: state?.appOnlineStatus?.isOnline,
        connectionState: state.ConnectionStateData.data,
        isEnableArchived: state?.webLocalStorageSetting?.isEnableArchived,
        commonData: state?.commonData,
        unreadCountData: state.unreadCountData,
        vCardData: state.vCardData.data,
        searchValue: state.chatSearch.query,
        searchFiltered: state.chatSearch.searchFiltered,
        chatFilter: state.chatFilter.active,
    };
};

const mapDispatchToProps = {
    updateRecentChat: RecentChatUpdateAction,
    updateNotification: RecentChatUpdateNotification,
    updateRoster: RecentChatRoster,
    setChatSearch,
    clearChatSearch,
    setChatSearchFiltered,
    setChatFilter,
};

const ConnectedRecentChatSection = connect(mapStateToProps, mapDispatchToProps)(RecentChatSection);

/**
 * Hosts the mute-expiry sweep, which needs a hook and a section that lives as long as
 * the chat list does.
 *
 * @param props Passed straight through to the section.
 * @returns {React.ReactElement} The chat list.
 */
const RecentChatSectionWithMuteExpiry = props => {
    useMuteExpiry();
    return <ConnectedRecentChatSection {...props} />;
};

export default RecentChatSectionWithMuteExpiry;

RecentChatSection.propTypes = {
    handleConversationSectionDisplay: PropTypes.func,
    callUserJID: PropTypes.string,
    handleShowCallScreen: PropTypes.func,
    callType: PropTypes.string,
    messageData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    newChatStatus: PropTypes.bool,
    callMode: PropTypes.string,
    pageType: PropTypes.string,
    unreadCountData: PropTypes.shape({
        unreadDataObj: PropTypes.shape({
            fromUserId: PropTypes.shape({
                count: PropTypes.number,
            }),
        }),
    }),
    featureStateData: PropTypes.shape({
        isRecentChatSearchEnabled: PropTypes.bool,
    }),
    groupsData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    broadCastData: PropTypes.object,
    rosterData: PropTypes.object,
    recentChatData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    commonData: PropTypes.object,
    updateRoster: PropTypes.func,
    isAppOnline: PropTypes.bool,
    callStatus: PropTypes.string,
    handlePopupState: PropTypes.func,
    searchValue: PropTypes.string,
    searchFiltered: PropTypes.bool,
    chatFilter: PropTypes.string,
    setChatFilter: PropTypes.func,
    clearChatSearch: PropTypes.func,
    setChatSearchFiltered: PropTypes.func,
};

// Shown when the chip filter matched nothing. Kept separate from the "no chats at all"
// state: telling someone with a full list that they have no conversations is misleading.
// The chip that filtered the list is still on screen, so clearing the filter is left to
// that chip rather than repeated here as a link.
const FilterEmptyState = () => {
    const { t } = useLanguage();

    return <p className="filter-empty">{t('chat.filter.NO_MATCH_TITLE')}</p>;
};

/* Named apart from the standalone RecentSearch.jsx, which call logs and the participant
 * picker use: that one keeps its value in local state, this one in chatSearchSlice. */
const ChatListSearch = ({ placeHolder }) => {
    const dispatch = useDispatch();
    const searchValue = useSelector(state => state.chatSearch.query);

    const searchFilterList = e => {
        const { value } = e.target;
        dispatch(setChatSearchFiltered(false));
        dispatch(setChatSearch(value));
    };

    const resetInputField = () => {
        dispatch(setChatSearchFiltered(false));
        dispatch(clearChatSearch());
    };

    return (
        <div className={`search-container ${searchValue && 'typingValue'}`}>
            <div className="search">
                {!searchValue && <Search className="search-icon" />}
                <input
                    type="text"
                    className="search-contacts"
                    name="search-contacts"
                    autoComplete="off"
                    placeholder={`${placeHolder || 'Search'}`}
                    value={searchValue}
                    onChange={searchFilterList}
                />
                <i
                    className="CloseSearch"
                    onClick={resetInputField}
                    onKeyDown={e => e.key === 'Enter' && resetInputField}
                    tabIndex="0"
                    role="button"
                >
                    {searchValue && <Close />}
                </i>
            </div>
        </div>
    );
};
